import type { z } from 'zod'
import { tokenStore } from '@/features/auth/model/tokenStore'
import { getPublicKey } from '@/shared/crypto/publicKey'
import { getDeviceToken } from '@/shared/crypto/deviceToken'
import { getFingerprint } from '@/shared/crypto/fingerprint'
import { encryptPayload } from '@/shared/crypto/publicKeyEncrypt'
import { ApiError, AuthError } from './errors'

/**
 * Centralized Monosan fetch wrapper.
 *
 * - Adds `Authorization: Bearer <accessToken>` from `tokenStore` (skipped via
 *   `skipAuth` for endpoints that don't require it, e.g. login).
 * - Adds `device-token` (UUID, see `deviceToken.ts`).
 * - Adds `x-fingerprint` (FingerprintJS visitorId, see `fingerprint.ts`).
 * - Validates JSON response against the caller-provided Zod schema.
 * - On 401: tries `onUnauthorized` once (refresh), then retries; if it still
 *   fails, clears the token store and throws `AuthError`.
 *
 */

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ?? 'https://slim-api.dev-n26.com'

type RefreshHandler = () => Promise<void>

let refreshHandler: RefreshHandler | null = null
let onClearAuth: (() => void) | null = null
let inFlightRefresh: Promise<void> | null = null

const sharedRefresh = (): Promise<void> => {
  if (inFlightRefresh) return inFlightRefresh
  inFlightRefresh = refreshHandler!().finally(() => {
    inFlightRefresh = null
  })
  return inFlightRefresh
}

export const configureFetch = (handlers: {
  onUnauthorized?: RefreshHandler
  onClearAuth?: () => void
}): void => {
  refreshHandler = handlers.onUnauthorized ?? null
  onClearAuth = handlers.onClearAuth ?? null
}

export type MonosanFetchInit<T extends z.ZodTypeAny> = Omit<
  RequestInit,
  'body'
> & {
  body?: Record<string, unknown> | null
  schema: T
  skipAuth?: boolean
  requiresCookie?: boolean
}

const MUTABLE_METHODS = new Set(['POST', 'PUT', 'PATCH'])

const buildHeaders = async (
  base: HeadersInit | undefined,
  skipAuth: boolean
): Promise<Headers> => {
  const headers = new Headers(base)
  headers.set('Accept', 'application/json')

  const deviceToken = getDeviceToken()
  headers.set('device-token', deviceToken)

  try {
    const fingerprint = await getFingerprint()
    headers.set('x-fingerprint', fingerprint)
  } catch {
    // Fingerprint failures must not block API requests.
  }

  if (!skipAuth) {
    const tokens = tokenStore.get()
    if (tokens) headers.set('Authorization', `Bearer ${tokens.accessToken}`)
  }

  return headers
}

const parseBody = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type') ?? ''
  if (response.status === 204 || !contentType.includes('json')) return null
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

const doFetch = async <T extends z.ZodTypeAny>(
  path: string,
  init: MonosanFetchInit<T>
): Promise<Response> => {
  // Destructure our custom fields so restInit satisfies RequestInit
  const { body, schema: _schema, skipAuth, requiresCookie, ...restInit } = init
  const headers = await buildHeaders(restInit.headers, skipAuth ?? false)
  const credentials = requiresCookie ? 'include' : 'same-origin'

  if (
    MUTABLE_METHODS.has((restInit.method ?? 'GET').toUpperCase()) &&
    body != null
  ) {
    const encryptedData = await encryptPayload(
      JSON.stringify(body),
      getPublicKey()
    )
    headers.set('Content-Type', 'application/json')
    return fetch(API_BASE + path, {
      ...restInit,
      headers,
      credentials,
      body: JSON.stringify({ encryptedData }),
    })
  }

  return fetch(API_BASE + path, { ...restInit, headers, credentials })
}

export async function monosanFetch<T extends z.ZodTypeAny>(
  path: string,
  init: MonosanFetchInit<T>
): Promise<z.infer<T>> {
  let response = await doFetch(path, init)

  if (response.status === 401 && !init.skipAuth && refreshHandler) {
    try {
      await sharedRefresh()
      response = await doFetch(path, init)
    } catch {
      onClearAuth?.()
      const body = await parseBody(response)
      throw new AuthError('Session expired', body)
    }
  }

  const body = await parseBody(response)

  if (!response.ok) {
    if (response.status === 401) {
      onClearAuth?.()
      throw new AuthError('Unauthorized', body)
    }
    throw new ApiError(response.status, response.statusText, body)
  }

  const parsed = init.schema.safeParse(body)
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      'Response did not match expected schema',
      { issues: parsed.error.issues, body }
    )
  }
  return parsed.data
}
